import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  List,
  ListItem,
  Divider,
  useColorModeValue,
  InputGroup,
  Input,
  InputLeftElement,
} from '@chakra-ui/react';
import { SearchIcon, PhoneIcon, EmailIcon } from '@chakra-ui/icons';
import { useOrders } from '../../contexts/OrderContext';
import { useAuth } from '../../contexts/AuthContext';
import { Order, OrderStatus } from '../../types/order';

const OrderStatusBadge: React.FC<{ status: OrderStatus }> = ({ status }) => {
  const statusColors = {
    pending: 'yellow',
    confirmed: 'blue',
    processing: 'purple',
    ready: 'orange',
    completed: 'green',
    cancelled: 'red',
  };

  return (
    <Badge colorScheme={statusColors[status]}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

const OrdersPage: React.FC = () => {
  const { user } = useAuth();
  const { getOrdersByFarmer, updateOrderStatus } = useOrders();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Get orders for the current farmer
  const farmerOrders = user ? getOrdersByFarmer(user.id) : [];

  // Filter orders based on status and search query
  const filteredOrders = farmerOrders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesSearch = order.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         order.buyerName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Sort orders by date (newest first)
  const sortedOrders = [...filteredOrders].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const handleStatusUpdate = async (orderId: string, newStatus: OrderStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      toast({
        title: 'Order Updated',
        description: 'Order status has been successfully updated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsDetailsModalOpen(false);
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: 'Failed to update order status. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
    setIsDetailsModalOpen(true);
  };

  return (
    <Box py={8}>
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          <Heading size="lg">Manage Orders</Heading>

          {/* Filters */}
          <HStack spacing={4}>
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
            <Select
              maxW="200px"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as OrderStatus | 'all')}
            >
              <option value="all">All Orders</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="processing">Processing</option>
              <option value="ready">Ready</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </HStack>

          {/* Orders Table */}
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Order Date</Th>
                  <Th>Product</Th>
                  <Th>Buyer</Th>
                  <Th>Quantity</Th>
                  <Th>Total Price</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedOrders.map((order) => (
                  <Tr key={order.id}>
                    <Td>{new Date(order.createdAt).toLocaleDateString()}</Td>
                    <Td>{order.productName}</Td>
                    <Td>{order.buyerName}</Td>
                    <Td>{order.quantity} {order.unit}</Td>
                    <Td>${order.totalPrice}</Td>
                    <Td>
                      <OrderStatusBadge status={order.status} />
                    </Td>
                    <Td>
                      <Button
                        size="sm"
                        colorScheme="brand"
                        onClick={() => handleViewDetails(order)}
                      >
                        View Details
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            {sortedOrders.length === 0 && (
              <Box textAlign="center" py={8}>
                <Text color="gray.500">No orders found matching your criteria</Text>
              </Box>
            )}
          </Box>
        </VStack>

        {/* Order Details Modal */}
        <Modal isOpen={isDetailsModalOpen} onClose={() => setIsDetailsModalOpen(false)} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Order Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedOrder && (
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontWeight="bold">Order ID:</Text>
                    <Text>{selectedOrder.id}</Text>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold">Product Details:</Text>
                    <Text>{selectedOrder.productName}</Text>
                    <Text>Quantity: {selectedOrder.quantity} {selectedOrder.unit}</Text>
                    <Text>Total Price: ${selectedOrder.totalPrice}</Text>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold">Buyer Information:</Text>
                    <List spacing={2}>
                      <ListItem>
                        <Text>{selectedOrder.buyerName}</Text>
                      </ListItem>
                      <ListItem>
                        <HStack>
                          <PhoneIcon />
                          <Text>{selectedOrder.buyerContact.phone}</Text>
                        </HStack>
                      </ListItem>
                      <ListItem>
                        <HStack>
                          <EmailIcon />
                          <Text>{selectedOrder.buyerContact.email}</Text>
                        </HStack>
                      </ListItem>
                    </List>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold">Delivery Details:</Text>
                    <Text>Location: {selectedOrder.deliveryLocation}</Text>
                    {selectedOrder.preferredDeliveryDate && (
                      <Text>Preferred Delivery Date: {new Date(selectedOrder.preferredDeliveryDate).toLocaleDateString()}</Text>
                    )}
                    {selectedOrder.notes && (
                      <Text>Notes: {selectedOrder.notes}</Text>
                    )}
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold" mb={2}>Update Status:</Text>
                    <Select
                      value={selectedOrder.status}
                      onChange={(e) => handleStatusUpdate(selectedOrder.id, e.target.value as OrderStatus)}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="ready">Ready</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </Select>
                  </Box>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setIsDetailsModalOpen(false)}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
};

export default OrdersPage;